/**
 * Company entity: backend DTOs, API calls, and the DTO -> display mapper.
 *
 * The display `Company` (src/app/types.ts) is a flat card shape; the backend
 * nests the derived numbers under `stats` (computed server-side at read time)
 * and carries fields the card doesn't show (region, declaredHeadcount).
 */

import type { Company } from '../../app/types';
import { apiGet, apiPost, apiPut } from '../apiClient';
import type { EntityQuestionValue, EntityQuestionsResponse } from './entityQuestions';
import type { OnboardingQuestionKind, OnboardingQuestionOption } from './onboarding';
import { planToDisplay } from './format';

/** Backend region enum (kebab on the wire, unusually — matches the DTO contract). */
export type CompanyRegion = 'england-wales' | 'scotland';

/** Backend billing plan enum. */
export type CompanyPlan = 'free' | 'business';

/** Legal form of the business (camelCase on the wire — matches the enum contract). */
export type CompanyType =
  | 'soleTrader'
  | 'partnership'
  | 'limitedCompany'
  | 'llp'
  | 'plc'
  | 'charity'
  | 'other';

/** CompanyType options in display order, for <select>s (onboarding + portfolio form). */
export const COMPANY_TYPES: { value: CompanyType; label: string }[] = [
  { value: 'soleTrader', label: 'Sole trader' },
  { value: 'partnership', label: 'Partnership' },
  { value: 'limitedCompany', label: 'Limited company (Ltd)' },
  { value: 'llp', label: 'Limited liability partnership (LLP)' },
  { value: 'plc', label: 'Public limited company (PLC)' },
  { value: 'charity', label: 'Charity' },
  { value: 'other', label: 'Other' },
];

/**
 * True for the types that carry a Companies House number (Ltd / LLP / PLC).
 * Mirrors the backend's CompanyTypes.IsRegistered helper — drives whether the
 * onboarding/portfolio forms show a company-number field.
 */
export function isRegisteredCompanyType(type: CompanyType): boolean {
  return type === 'limitedCompany' || type === 'llp' || type === 'plc';
}

/** Read-time rollup the server computes from the company's employees/docs/cases. */
export interface CompanyStatsDto {
  headcount: number;
  compliance: number;
  openActions: number;
  needsAttention: number;
}

export interface CompanyDto {
  id: string;
  name: string;
  sector: string;
  location: string;
  region: CompanyRegion;
  companyType: CompanyType;
  companyNumber?: string | null;
  declaredHeadcount: number;
  /** Active employees on the team list (CompanyStats.Headcount). */
  employeeHeadcount: number;
  /** max(declaredHeadcount, employeeHeadcount) — the number Reeve actually uses. */
  effectiveHeadcount: number;
  plan: CompanyPlan;
  stats: CompanyStatsDto;
  createdAt: string;
}

/** A findings question with its currently-stored answer ("" when unanswered). */
export interface OnboardingQuestionWithValue {
  key: string;
  prompt: string;
  kind: OnboardingQuestionKind;
  options?: OnboardingQuestionOption[] | null;
  help?: string | null;
  value: string;
}

/**
 * The full editable view of a company (GET /api/companies/{id}/config): the
 * structured profile fields plus the findings questions with their answers.
 */
export interface CompanyConfigDto {
  id: string;
  name: string;
  sector: string;
  location: string;
  region: CompanyRegion;
  companyType: CompanyType;
  companyNumber?: string | null;
  declaredHeadcount: number;
  employeeHeadcount: number;
  effectiveHeadcount: number;
  plan: CompanyPlan;
  questions: OnboardingQuestionWithValue[];
  /**
   * Every currently-applicable employer predicate question with its stored yes/no answer (null when
   * unanswered). Recomputed server-side from the answers, so flipping one to yes reveals the
   * questions it gates on the next response.
   */
  predicateQuestions: EntityQuestionValue[];
}

/**
 * PUT /api/companies/{id} body — all fields optional, only provided ones apply.
 * `answers` carries finding keys only; region/headcount are structured fields.
 */
export interface UpdateCompanyRequest {
  name?: string;
  sector?: string;
  region?: CompanyRegion;
  declaredHeadcount?: number;
  companyType?: CompanyType;
  /** Companies House number; '' clears it (e.g. when the type becomes unregistered). */
  companyNumber?: string;
  answers?: Record<string, string>;
  /**
   * Employer predicate answers, keyed by slug. OMIT to leave the stored answers untouched; sending
   * {} is a full replace with nothing and wipes them. Only send this once the questions have loaded.
   */
  predicates?: Record<string, boolean>;
}

export interface CreateCompanyRequest {
  name: string;
  companyType: CompanyType;
  /** Only meaningful for registered types (Ltd/LLP/PLC); omit otherwise. */
  companyNumber?: string;
  sector?: string;
  location?: string;
  region?: CompanyRegion;
  declaredHeadcount?: number;
  /** SIC codes from the onboarding Companies House lookup; register facts used to suggest answers. */
  sicCodes?: string[];
  /** Last filed accounts type from the same lookup ("micro-entity", "small", "full", "group"). */
  lastAccountsType?: string;
  /** FCA register status: 'authorised' | 'notAuthorised'; omit for "could not establish". */
  fcaStatus?: string;
}

/** GET /api/companies — the whole portfolio for the authenticated tenant. */
export function listCompanies(): Promise<CompanyDto[]> {
  return apiGet<CompanyDto[]>('/api/companies');
}

/** GET /api/companies/{id}. */
export function getCompany(id: string): Promise<CompanyDto> {
  return apiGet<CompanyDto>(`/api/companies/${id}`);
}

/** POST /api/companies. */
export function createCompany(request: CreateCompanyRequest): Promise<CompanyDto> {
  return apiPost<CompanyDto>('/api/companies', request);
}

/** GET /api/companies/{id}/config — the full editable view (profile + findings). */
export function getCompanyConfig(id: string): Promise<CompanyConfigDto> {
  return apiGet<CompanyConfigDto>(`/api/companies/${id}/config`);
}

/**
 * Which employer questions apply given answers the user has changed but NOT saved. Read-only: the
 * draft is evaluated and discarded. Lets the company page reveal a follow-up the moment its parent is
 * toggled to yes, without the browser having to reimplement the gating rule (which it could not do
 * anyway, since many questions hang off a derived parent the client cannot compute).
 */
export function previewCompanyQuestions(
  id: string,
  predicates: Record<string, boolean>,
): Promise<EntityQuestionsResponse> {
  return apiPost<EntityQuestionsResponse>(`/api/companies/${id}/questions/preview`, { predicates });
}

/** PUT /api/companies/{id} — apply the provided fields, returns the fresh config. */
export function updateCompany(
  id: string,
  request: UpdateCompanyRequest,
): Promise<CompanyConfigDto> {
  return apiPut<CompanyConfigDto>(`/api/companies/${id}`, request);
}

/** CompanyDto -> display Company (flatten stats, capitalise plan). */
export function mapCompany(dto: CompanyDto): Company {
  return {
    id: dto.id,
    name: dto.name,
    sector: dto.sector,
    location: dto.location,
    // The chip/cards show the USED count — effective headcount, not raw employees.
    employees: dto.effectiveHeadcount,
    compliance: dto.stats.compliance,
    openActions: dto.stats.openActions,
    plan: planToDisplay(dto.plan),
  };
}
